import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Sparkles, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Section, Btn, Input, Textarea, Select, Field, cardCls, StatusPill, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";
import FileUpload from "@/components/admin/FileUpload";
import ServiceFormatsEditor from "@/components/admin/ServiceFormatsEditor";

const CATS = ["Business", "Innovation", "Technology", "Retail", "Property", "Robotics", "Entrepreneurship", "Group News"];
const EMPTY = { title: "", category: "Business", excerpt: "", body: "", cover_url: null, status: "draft", ai_generated: false };

export default function InsightsAdmin() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: "", category: "Business", notes: "" });
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => api.get("/admin/articles").then(({ data }) => setArticles(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditing(a.id); };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    if (editing === "new") await api.post("/admin/articles", form);
    else await api.patch(`/admin/articles/${editing}`, form);
    toast.success("Article saved"); setEditing(null); load();
  };
  const publish = async (a, status) => { await api.patch(`/admin/articles/${a.id}`, { status }); toast.success(status === "published" ? "Published" : "Moved to draft"); load(); if (editing === a.id) setForm((f) => ({ ...f, status })); };
  const remove = async (a) => { if (!window.confirm("Delete article?")) return; await api.delete(`/admin/articles/${a.id}`); load(); };

  const runAi = async () => {
    if (!aiForm.topic) { toast.error("Enter a topic"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/articles/ai-draft", aiForm);
      toast.success("AI draft created (saved as draft — needs your approval to publish)");
      setAiOpen(false); setAiForm({ topic: "", category: "Business", notes: "" });
      load();
      openEdit(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAiBusy(false); }
  };

  return (
    <div data-testid="admin-insights">
      <ServiceFormatsEditor />
      <Section
        title="Website Content — Insights"
        subtitle="Write articles manually or draft with AI. AI drafts always require your approval before publishing."
        action={<div className="flex gap-3">
          <Btn variant="outline" data-testid="ai-draft-open" onClick={() => setAiOpen(true)}><Sparkles size={15} /> AI Draft</Btn>
          <Btn data-testid="article-add" onClick={openNew}><Plus size={15} /> New Article</Btn>
        </div>}
      >
        {articles.length === 0 ? <EmptyState>No articles yet.</EmptyState> : (
          <div className={`${cardCls} divide-y divide-white/5`}>
            {articles.map((a) => (
              <div key={a.id} data-testid={`article-row-${a.slug}`} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-bd-ivory truncate">{a.title}</span>
                    <StatusPill status={a.status} />
                    {a.ai_generated && <span className="text-[0.6rem] uppercase tracking-wider text-bd-gold/70 flex items-center gap-1"><Sparkles size={10} /> AI</span>}
                  </div>
                  <p className="text-xs text-bd-ivory/40 mt-0.5">{a.category}</p>
                </div>
                {a.status === "draft"
                  ? <Btn data-testid={`article-publish-${a.slug}`} onClick={() => publish(a, "published")} className="!py-1.5 !px-3">Publish</Btn>
                  : <Btn variant="outline" onClick={() => publish(a, "draft")} className="!py-1.5 !px-3">Unpublish</Btn>}
                <a href={`/insights/${a.slug}`} target="_blank" rel="noreferrer" className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Eye size={16} /></a>
                <button onClick={() => openEdit(a)} className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Pencil size={16} /></button>
                <button onClick={() => remove(a)} className="p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Editor */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "New Article" : "Edit Article"} wide testId="article-modal">
        <div className="space-y-5">
          <Field label="Title"><Input data-testid="article-title" value={form.title} onChange={set("title")} /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Category"><Select value={form.category} onChange={set("category")}>{CATS.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Cover image"><FileUpload value={form.cover_url} onChange={(url) => setForm((f) => ({ ...f, cover_url: url }))} label="Upload cover" /></Field>
          </div>
          <Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} /></Field>
          <Field label="Body (markdown supported)"><Textarea rows={12} value={form.body} onChange={set("body")} className="font-mono text-xs" /></Field>
          <div className="flex items-center justify-between">
            <StatusPill status={form.status} />
            <div className="flex gap-3">
              <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn data-testid="article-save" onClick={save}>Save Draft</Btn>
            </div>
          </div>
        </div>
      </Modal>

      {/* AI draft */}
      <Modal open={aiOpen} onClose={() => setAiOpen(false)} title="AI Writing Assistant" testId="ai-modal">
        <div className="space-y-5">
          <p className="text-sm text-bd-ivory/50">Draft a polished, on-brand article. It will be saved as a <span className="text-bd-gold">draft</span> for your review — never published automatically.</p>
          <Field label="Topic"><Input data-testid="ai-topic" value={aiForm.topic} onChange={(e) => setAiForm((f) => ({ ...f, topic: e.target.value }))} placeholder="e.g. Why we build across multiple industries" /></Field>
          <Field label="Category"><Select value={aiForm.category} onChange={(e) => setAiForm((f) => ({ ...f, category: e.target.value }))}>{CATS.map((c) => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Direction / notes (optional)"><Textarea rows={3} value={aiForm.notes} onChange={(e) => setAiForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
          <div className="flex justify-end gap-3">
            <Btn variant="outline" onClick={() => setAiOpen(false)}>Cancel</Btn>
            <Btn data-testid="ai-generate" onClick={runAi} disabled={aiBusy}>{aiBusy ? <><Loader2 size={15} className="animate-spin" /> Drafting…</> : <><Sparkles size={15} /> Generate Draft</>}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
